import React, { useEffect, useState, useCallback, useRef } from 'react'; // Added useCallback
import { Card, Button, Form, Row, Col, InputGroup, Modal } from 'react-bootstrap';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { Korean } from 'flatpickr/dist/l10n/ko.js';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter_paging';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination_paging';
import data from 'safedb/SA/sampledata/sampledataLogAdmin';
import CheckOutModal from 'components/modal/CheckOutModal';
import { fetchManageLog } from 'hooks/CallApi';
import styled from 'styled-components';
import {
  formatDate_log,
  formatToCustomISO
} from 'components/common/SunnyUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from 'providers/AppProvider';

const columns = [
  {
    accessorKey: 'num',
    header: '순번',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'jobdate',
    header: '작업일시',
    meta: { maxlength: 19, headerProps: { className: 'text-900' } }
  },
  // { accessorKey: 'org', header: '소속', meta: { headerProps: { className: 'text-900' } } },
  {
    accessorKey: 'Drole',
    header: '역할',
    meta: { maxlength: 7, headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'uid',
    header: '아이디',
    meta: { maxlength: 20, headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'job',
    header: '작업내역',
    meta: { maxlength: 20, headerProps: { className: 'text-900' } },
    enableSorting: false
  },
  {
    accessorKey: 'result',
    header: '결과',
    meta: { headerProps: { className: 'text-900' } },
    enableSorting: false
  }
];

// JSON 표시 컴포넌트
const JsonDisplay = ({ data }) => {
  if (!data) return <div>요청 상세 데이터가 없습니다.</div>;

  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    return (
      <pre style={{
        whiteSpace: 'pre-wrap',
        fontSize: '12px',
        backgroundColor: '#f8f9fa',
        padding: '15px',
        borderRadius: '4px',
        border: '1px solid #dee2e6',
        maxHeight: '400px',
        overflow: 'auto',
        margin: 0
      }}>
        {jsonString}
      </pre>
    );
  } catch (error) {
    return <div>JSON 파싱 오류: {error.message}</div>;
  }
};

function AdvanceTableExample() {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [requestDateRange, setRequestDateRange] = useState([]);
  const [isDateCleared, setIsDateCleared] = useState(false);
  const [policyId, setPolicyId] = useState('');
  const [org, setOrg] = useState('');
  const [role, setRole] = useState('');
  const [uid, setUid] = useState('');
  const [job, setJob] = useState('');
  const [options, setOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState('직접입력'); // 선택된 옵션 상태 추가


  const [result, setResult] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [modalReShow, setModalReShow] = useState(false);
  const [roles, setRoles] = useState([]); // 역할 데이터 상태 추가.
  const roleMapping = {
    '시스템 관리자': 'SA',
    '최고 관리자': 'ADMIN',
    '운영 관리자': 'MANAGER'
  };

  const { config } = useAppContext();
  const { userAuth } = config;

  const [roleOptions, setRoleOptions] = useState([]); // 셀렉트 박스에 표시할 역할 목록

  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // 현재 페이지
  const [perPage, setPerPage] = useState(10); // 페이지당 항목 수 (size)
  const [resultShow, setResultShow] = useState(false);

  // 모달 상태 관리
  const [detailModalShow, setDetailModalShow] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  // 검색 확정 필터 (SA 페이지와 동일 패턴 적용)
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: null,
    endDate: null,
    role: '',
    uid: '',
    job: '',
    result: ''
  });
  const [searchApplied, setSearchApplied] = useState(false);

  const table = useAdvanceTable({
    data: filteredData,
    columns,
    sortable: true,
    selection: false,
    pagination: true,
    perPage,
    selectionColumnWidth: 30
  });

  // 사용자 권한이 변경될 때마다 역할 옵션을 업데이트
  useEffect(() => {
    const filteredRoleOptions = userAuth === 'SA'
      ? Object.keys(roleMapping) // SA는 모든 역할을 볼 수 있음
      : Object.keys(roleMapping).filter(roleName => roleName !== '시스템 관리자'); // SA가 아니면 시스템 관리자 제외
    setRoleOptions(filteredRoleOptions);

    // 현재 선택된 역할이 시스템 관리자이고 사용자 권한이 SA가 아닌 경우 초기화
    if (userAuth !== 'SA' && role === '시스템 관리자') {
      setRole('');
    }
  }, [userAuth, role]);

  useEffect(() => {
    // 텍스트 파일에서 옵션을 읽어오는 함수
    const fetchOptions = async () => {
      try {
        const response = await fetch('/options.txt');
        const text = await response.text();
        const optionsArray = text.split('\n').map(option => option.trim()).filter(option => option);
        setOptions(['직접입력', ...optionsArray]); // "직접입력" 옵션 추가
      } catch (error) {
        console.error('옵션을 불러오는 중 에러 발생:', error);
      }
    };

    fetchOptions();
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setJob(value);

    // 입력 값이 빈 문자열일 때 선택 상태 초기화
    if (value === '') {
      setSelectedOption('직접입력');
    }
  };

  const handleSelectChange = (e) => {
    const value = e.target.value;
    setSelectedOption(value); // 선택된 옵션 상태 업데이트
    if (value !== '직접입력') {
      setJob(value); // 입력 필드 값도 업데이트
    } else {
      setJob(''); // "직접입력" 선택 시 입력 필드 초기화
    }
  };

  // 필터 영역에서 Enter 키로 검색 실행
  const handleFilterKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // API 호출 함수 (SA 페이지 패턴: 검색 확정 후에만 호출)
  const loadData = useCallback(async (page, size) => { // Wrapped with useCallback
    if (!searchApplied) return;
    try {
      const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // const response = await fetchManageLog(page, size); // API 호출
      const response = await fetchManageLog({
        page,
        size,
        // 검색 확정된 필터만 사용 (기본값 주입 없음)
        searchStartTime: appliedFilters.startDate ? formatToCustomISO(appliedFilters.startDate) : null,
        searchEndTime: appliedFilters.endDate ? formatToCustomISO(appliedFilters.endDate) : null,
        timeZone: clientTimeZone,
        userId: appliedFilters.uid || null,
        userRole: appliedFilters.role ? roleMapping[appliedFilters.role] : null, // 운영 관리자용 role 매핑
        taskHistory: appliedFilters.job || null,
        result: appliedFilters.result || null,
        insertTime: 'desc'
      });

      const logData = response.data.content.map((item, index) => ({
        // num: index + 1 + page * size, // 순번 계산
        num: response.data.page.totalElements - (page * size + index), // 역순으로 순번 계산
        jobdate: item.insertTime
          ? formatDate_log(item.insertTime) // 포맷 함수 호출
          : '-',
        role: item.userRole,
        Drole:
          item.userRole === 'SA'
            ? '시스템 관리자'
            : item.userRole === 'ADMIN'
              ? '최고 관리자'
              : '운영 관리자',

        uid: item.userId,
        job: item.taskHistory,
        result:
          ['성공', '클라이언트 에러', '서버 에러', '기타'][item.result] ||
          '기타',
        requestDetails: item.requestDetails || null // requestDetails 추가
      }));
      setTotalElements(response.data.page.totalElements);
      setTableData(logData);
      setFilteredData(logData);

      // 역할 데이터 추출 및 중복 제거
      const uniqueRoles = [
        ...new Set(response.data.content.map(item => item.userRole))
      ];
      setRoles(uniqueRoles);
    } catch (error) {
      console.error('API 데이터 로드 에러:', error);
    }
  }, [appliedFilters, searchApplied, perPage]);

  // 페이지 변경 / 페이지 크기 변경 / 검색 확정 시 데이터 로드 (SA 페이지와 동일)
  useEffect(() => {
    loadData(currentPage, perPage);
  }, [searchApplied, currentPage, perPage, loadData]);

  // 초기에는 자동 조회하지 않음 (검색 확정 후 조회)

  const handlePageChange = useCallback(newPage => { // Wrapped with useCallback
    setCurrentPage(newPage);
    table.resetRowSelection(); // 선택된 행 초기화

    // 페이지 변경 시 모달 닫기
    setDetailModalShow(false);
    setSelectedRowData(null);
  }, [table, perPage]);

  const handlePerPageChange = useCallback(newPerPage => { // Wrapped with useCallback
    setPerPage(newPerPage); // 페이지 크기 업데이트
    setCurrentPage(0); // 첫 페이지로 이동

    // 페이지 크기 변경 시 모달 닫기
    setDetailModalShow(false);
    setSelectedRowData(null);
  }, []);

  const handleSearch = async () => {
    const [startDate, endDate] = requestDateRange;

    if (startDate && endDate && startDate > endDate) {
      // alert('시작 날짜는 종료 날짜 이전이어야 합니다.');
      setResultShow(true);
      return;
    }

    // 검색 시 모달 닫기
    setDetailModalShow(false);
    setSelectedRowData(null);
    // 검색 확정 값 저장 후 첫 페이지부터 조회
    setAppliedFilters({
      startDate: startDate || null,
      endDate: endDate || null,
      role,
      uid,
      job,
      result
    });
    setSearchApplied(true);
    setCurrentPage(0);
  };

  const handleDefault = () => {
    setRequestDateRange([]);
    setOrg('');
    setRole('');
    setUid('');
    setJob('');
    setResult('');
    setSelectedOption('직접입력'); // 작업내역 콤보박스를 직접입력으로 초기화
    // 검색 조건도 비우고 즉시 전체 기간(null)로 조회되도록 적용
    setAppliedFilters({
      startDate: null,
      endDate: null,
      role: '',
      uid: '',
      job: '',
      result: ''
    });
    setSearchApplied(false); // 초기화만, 자동 검색 X
    setCurrentPage(0);

    // 모달 닫기
    setDetailModalShow(false);
    setSelectedRowData(null);

    const datepicker_start =
      document.querySelector('#timepicker_start')._flatpickr;
    if (datepicker_start) {
      datepicker_start.clear(); // 기존 날짜 선택 제거
      datepicker_start.setDate([]); // 날짜 범위 초기화
    }
    const datepicker_end = document.querySelector('#timepicker_end')._flatpickr;
    if (datepicker_end) {
      datepicker_end.clear(); // 기존 날짜 선택 제거
      datepicker_end.setDate([]); // 날짜 범위 초기화
    }
  };

  // 모달 핸들러
  const handleRowClick = (row) => {
    setSelectedRowData(row);
    setDetailModalShow(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalShow(false);
    setSelectedRowData(null);
  };

  useEffect(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0); // 오늘 00시 00분 00초

    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999); // 오늘 23시 59분 59초
    setRequestDateRange([todayStart, todayEnd]);

    flatpickr('#timepicker_start', {
      mode: 'single',
      dateFormat: 'Y-m-d H:i',
      enableTime: true,
      time_24hr: true,
      disableMobile: true,
      position: 'below',
      locale: Korean,
      minDate: new Date().fp_incr(-180),
      defaultDate: todayStart,
      // onChange: (selectedDates) => setRequestDateRange(selectedDates)
      onChange: selectedDates => {
        setRequestDateRange(prev => [selectedDates[0], prev[1] || null]);
        setIsDateCleared(false);
      }
    });

    flatpickr('#timepicker_end', {
      mode: 'single',
      dateFormat: 'Y-m-d H:i',
      enableTime: true,
      time_24hr: true,
      disableMobile: true,
      position: 'below',
      locale: Korean,
      minDate: new Date().fp_incr(-180),
      defaultDate: todayEnd,
      // onChange: (selectedDates) => setRequestDateRange(selectedDates)
      onChange: selectedDates => {
        setRequestDateRange(prev => [prev[0] || null, selectedDates[0]]);
        setIsDateCleared(false);
      }
    });
  }, []);
  // 최초 진입 시 기본(오늘) 날짜로 자동 검색 트리거
  const didAutoSearchRef = useRef(false);
  useEffect(() => {
    if (didAutoSearchRef.current) return;
    if (requestDateRange && requestDateRange[0] && requestDateRange[1]) {
      didAutoSearchRef.current = true;
      handleSearch();
    }
  }, [requestDateRange, handleSearch]);

  const startInputRef = useRef(null);
  const endInputRef = useRef(null);

  return (
    <AdvanceTableProvider {...table}>
      <FilterContainer>
        <FilterInputs className="filter-inputs" onKeyDown={handleFilterKeyDown}>
          {/* 전체 레코드 수와 페이지네이션 셀렉트 */}
          <div className="d-flex align-items-center">
            <AdvanceTableFooter
              rowsPerPageSelection_filter
              onPageChange={(page, newPageSize) => {
                handlePerPageChange(newPageSize); // 페이지 크기 변경
                setCurrentPage(page); // 현재 페이지 설정
              }}
            />
          </div>
          {/* 검색 필터 */}
          <div className="mb-0 fs-10" style={{ position: 'relative' }}>

            <input
              id="timepicker_start"
              className="form-control datetimepicker"
              type="text"
              placeholder="2024-01-01 00:00"
              title="작업일시"
              ref={startInputRef} // input 요소 참조
              style={{ width: '11.5rem', height: '35px', fontSize: '1rem' }}
            />
            <span
              onClick={() => startInputRef.current?.click()}
              style={{
                cursor: 'pointer',
                position: 'absolute',
                left: '9.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1
              }}
            >
              <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px' }} />
            </span>
          </div>
          <div className="mb-0 fs-10" style={{ position: 'relative' }}>

            <input
              id="timepicker_end"
              className="form-control datetimepicker"
              type="text"
              placeholder="2024-12-31 00:00"
              title="작업일시"
              ref={endInputRef} // input 요소 참조
              style={{ width: '11.5rem', height: '35px', fontSize: '1rem' }}
            />
            <span
              onClick={() => endInputRef.current?.click()} // click() 호출
              style={{
                cursor: 'pointer',
                position: 'absolute',
                left: '9.5rem',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1
              }}
            >
              <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '5px' }} />
            </span>
          </div>

          <div className="mb-0 fs-10">
            <Form.Select
              size="sm"
              value={role}
              title="역할"
              onChange={e => setRole(e.target.value)}
              style={{ height: '35px', fontSize: '0.85rem', padding: '4px 2.25rem 4px 8px' }}
            >
              <option value="" disabled hidden>
                역할
              </option>
              <option value="">전체</option>
              {roleOptions.map((roleName, index) => (
                <option key={index} value={roleName}>
                  {roleName}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="mb-0 fs-10">
            <Form.Control
              size="sm"
              type="text"
              title="아이디"
              value={uid}
              placeholder="아이디"
              onChange={e => setUid(e.target.value)}
              style={{ height: '35px' }}
              maxLength="30"
            />
          </div>
          <div className="mb-0 fs-10">
            {/* <Form.Control
              size="sm"
              type="text"
              title="작업내역"
              value={job}
              placeholder="작업내역"
              onChange={e => setJob(e.target.value)}
              style={{ height: '35px' }}
              maxlength="20"
            /> */}
            <InputGroup style={{ width: '100%' }}>
              <Form.Control
                size="sm"
                type="text"
                title="작업내역"
                value={job}
                placeholder="작업내역"
                onChange={handleInputChange}
                style={{ height: '35px' }}
                maxLength="20"
                disabled={selectedOption !== '직접입력'} // "직접입력"이 아닌 경우 비활성화
              />
              <Form.Select
                size="sm"
                value={selectedOption} // 선택된 옵션 상태 사용
                onChange={handleSelectChange}
                style={{ height: '35px', fontSize: '0.85rem', padding: '4px 2.25rem 4px 8px' }}
              >
                {options.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          </div>
        </FilterInputs>

        {/* 구분선 */}

        <FilterButtons>
          <FilterButton
            variant="outline-secondary"
            onClick={handleDefault}
            title="초기화"
            style={{ height: '35px', paddingTop: 0, paddingBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            초기화
          </FilterButton>
          <FilterButton variant="outline-secondary" onClick={handleSearch} style={{ height: '35px', paddingTop: 0, paddingBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            검색
          </FilterButton>
          {/* CSV 버튼 - 나중에 활성화 가능하도록 주석 처리 */}
          {/* <FilterButton variant="outline-secondary" style={{ height: '35px', paddingTop: 0, paddingBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>CSV</FilterButton> */}
        </FilterButtons>
      </FilterContainer>

      <AdvanceTable
        headerClassName="bg-200 text-nowrap align-middle"
        rowClassName="align-middle white-space-nowrap"
        tableProps={{ striped: true, className: 'fs-10 mb-0 overflow-hidden' }}
        onRowClick={handleRowClick}
      />
      <p className="mb-0" style={{ fontWeight: 700, lineHeight: '29px' }}>
        총 {totalElements.toLocaleString()}건
      </p>
      {/* <AdvanceTablePagination /> */}
      <AdvanceTablePagination
        currentPage={currentPage}
        totalItems={totalElements}
        itemsPerPage={perPage}
        onChange={newPage => handlePageChange(newPage)}
      />
      <CheckOutModal
        show={resultShow}
        onHide={() => setResultShow(false)}
        onConfirm={() => setResultShow(false)}
        title="경고"
        body="시작 날짜는 종료 날짜 이전이어야 합니다."
        confirmText="확인"
        variant="warning"
        SubmitButton={true}
      />

      {/* 상세 정보 모달 */}
      <Modal
        show={detailModalShow}
        onHide={handleCloseDetailModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>로그 상세 정보</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRowData ? (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>순번:</strong> {selectedRowData.original?.num || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>작업일시:</strong> {selectedRowData.original?.jobdate || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>역할:</strong> {selectedRowData.original?.Drole || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>아이디:</strong> {selectedRowData.original?.uid || 'N/A'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>작업내역:</strong> {selectedRowData.original?.job || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>결과:</strong> {selectedRowData.original?.result || 'N/A'}
                </Col>
              </Row>
              <hr />
              <div>
                <h6 className="mb-3">요청 상세 데이터</h6>
                <JsonDisplay data={selectedRowData.original?.requestDetails} />
              </div>
            </div>
          ) : (
            <div>데이터를 불러오는 중...</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDetailModal}>
            닫기
          </Button>
        </Modal.Footer>
      </Modal>
    </AdvanceTableProvider>
  );
}

const logManage = () => (
  <div className="container mt-5">
    <Card className="mypage-card">
      <Card.Header as="h4">관리 로그</Card.Header>
      <Card.Body>
        <AdvanceTableExample />
      </Card.Body>
    </Card>
  </div>
);

export default logManage;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 0.25rem;
  gap: 8px;

  /* @media (max-width: 1919px) {
    flex-direction: column;
    margin-bottom: 16px;
  } */
`;

const FilterInputs = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  gap: 8px;
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 0.2rem;

  @media (max-width: 1919px) {
    gap: 4px;
  }
`;

const FilterButton = styled(Button)`
  white-space: nowrap;
  
  @media (max-width: 1919px) {
    line-height: 0px; /* 버튼 높이와 동일하게 설정 */
    height:30px;
    padding: 4px 8px;
  }
`;
